import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useSEO } from "@/lib/useSEO";
import { SEO_PAGES, SEO_FOOTER_LINKS, buildSeoJsonLd } from "@/pages/seo/seoContent";
import { ArrowRight, Check, Plus, Lock, ShieldCheck, EyeSlash } from "@phosphor-icons/react";

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.55, delay, ease: [0.16, 1, 0.3, 1] },
});

function SeoFooter() {
  return (
    <footer className="relative border-t border-white/10 bg-black/60 py-16 sm:py-20" data-testid="seo-footer">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 md:grid-cols-3 gap-10">
        <div>
          <Link to="/" data-testid="footer-logo-link" className="flex items-center gap-2.5 mb-4 w-fit group">
            <img src="/controlly-icon.png" alt="Controlly" className="w-9 h-9 rounded-md group-hover:scale-105 transition-transform" />
            <span className="font-heading text-2xl font-extrabold tracking-tight">Controlly</span>
          </Link>
          <p className="text-sm text-muted-foreground max-w-sm">Remote access without the enterprise price tag. For families, freelancers, and small businesses.</p>
          <Link to="/login" data-testid="seo-footer-get-started-cta" className="mt-5 inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground text-sm font-bold px-5 py-2.5 hover:bg-primary/90 hover:scale-[1.04] transition-all w-fit">
            Start free <ArrowRight size={15} weight="bold" />
          </Link>
        </div>
        <div>
          <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-white/70 mb-4">Explore</h2>
          <ul className="space-y-2.5">
            {SEO_FOOTER_LINKS.map((l) => (
              <li key={l.slug}>
                <Link to={SEO_PAGES[l.slug].path} data-testid={`footer-seo-link-${l.slug}`} className="text-sm text-muted-foreground hover:text-white transition-colors">
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div className="flex flex-col gap-3">
          <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-white/70 mb-1">Company</h2>
          <a href="mailto:contact@getcontrolly.com" className="text-sm text-muted-foreground hover:text-white transition-colors" data-testid="footer-support-email">contact@getcontrolly.com</a>
          <Link to="/support" data-testid="footer-enter-code-link" className="text-sm text-primary hover:text-white transition-colors font-medium">Have a support code? Enter it here →</Link>
          <div className="flex flex-wrap gap-x-4 gap-y-2 text-xs text-muted-foreground/70 mt-2">
            <Link to="/#pricing" className="hover:text-white transition-colors">Pricing</Link>
            <Link to="/login" className="hover:text-white transition-colors">Log in</Link>
            <Link to="/terms" className="hover:text-white transition-colors" data-testid="footer-terms-link">Terms</Link>
            <Link to="/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy-link">Privacy</Link>
            <Link to="/refund-policy" className="hover:text-white transition-colors" data-testid="footer-refund-link">Refunds</Link>
            <Link to="/acceptable-use" className="hover:text-white transition-colors" data-testid="footer-aup-link">Acceptable Use</Link>
            <Link to="/cookies" className="hover:text-white transition-colors" data-testid="footer-cookies-link">Cookies</Link>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-5 sm:px-8 mt-12 pt-6 border-t border-white/5 flex flex-col sm:flex-row justify-between gap-2 text-xs text-muted-foreground/60">
        <span>© {new Date().getFullYear()} Controlly. All rights reserved.</span>
        <span className="font-mono">getcontrolly.com</span>
      </div>
    </footer>
  );
}

export default function SeoLanding({ slug: slugProp }) {
  const params = useParams();
  const slug = slugProp || params.slug;
  const page = SEO_PAGES[slug];

  useSEO({
    title: page?.title,
    description: page?.description,
    path: page?.path,
    jsonLd: page ? buildSeoJsonLd(page) : null,
  });

  if (!page) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-background text-foreground antialiased overflow-x-hidden">
      {/* Navbar */}
      <header className="fixed inset-x-0 top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-white/10">
        <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo">
            <img src="/controlly-icon.png" alt="Controlly" className="w-8 h-8 rounded-md" />
            <span className="font-heading text-lg font-extrabold tracking-tight">Controlly</span>
          </Link>
          <div className="flex items-center gap-2 sm:gap-4">
            <Link to="/#pricing" className="hidden sm:block text-sm text-muted-foreground hover:text-white transition-colors">Pricing</Link>
            <Link to="/login" data-testid="nav-login-button" className="hidden sm:block text-sm text-muted-foreground hover:text-white transition-colors">Log in</Link>
            <Link
              to="/login"
              data-testid="nav-get-started-cta"
              className="inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground text-sm font-bold px-4 py-2 hover:bg-primary/90 hover:scale-[1.04] transition-all"
            >
              Start free <ArrowRight size={15} weight="bold" />
            </Link>
          </div>
        </nav>
      </header>

      {/* Hero */}
      <section className="relative pt-36 pb-16 sm:pt-44 sm:pb-20">
        <div className="absolute inset-0 session-grid-bg opacity-40" />
        <div className="absolute inset-0 grain-overlay opacity-[0.06] pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-5 sm:px-8">
          <nav aria-label="Breadcrumb" className="mb-6 font-mono text-xs text-muted-foreground/70">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span className="mx-2">/</span>
            <span className="text-muted-foreground">{page.h1}</span>
          </nav>
          <motion.h1 {...fade(0)} className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[0.98] tracking-tighter" data-testid="seo-h1">
            {page.h1}
          </motion.h1>
          <motion.p {...fade(0.08)} className="mt-6 max-w-2xl text-base md:text-lg text-muted-foreground leading-relaxed">
            {page.lead}
          </motion.p>
          <motion.div {...fade(0.14)} className="mt-8 flex flex-wrap items-center gap-3">
            <Link
              to={page.cta?.to || "/login"}
              data-testid="seo-hero-cta"
              className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground font-semibold px-6 py-3 hover:scale-[1.04] transition-transform"
            >
              {page.cta?.label || "Start free"} <ArrowRight size={17} weight="bold" />
            </Link>
          </motion.div>
          <motion.div {...fade(0.2)} className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-muted-foreground">
            {[
              { icon: Lock, label: "Encrypted sessions" },
              { icon: ShieldCheck, label: "One-time pairing tokens" },
              { icon: EyeSlash, label: "On-screen access indicator" },
            ].map(({ icon: Icon, label }) => (
              <span key={label} className="inline-flex items-center gap-1.5">
                <Icon size={15} weight="fill" className="text-emerald-400/80" />
                {label}
              </span>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Content sections */}
      <section className="relative py-8 sm:py-12 border-t border-white/10">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 space-y-10">
          {page.sections.map((s, i) => (
            <motion.div key={i} {...fade(0.05 * i)} data-testid={`seo-section-${i}`}>
              <h2 className="font-heading text-2xl lg:text-3xl font-bold tracking-tight">{s.h}</h2>
              <p className="mt-3 text-base text-muted-foreground leading-relaxed">{s.p}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Features */}
      {page.features?.length > 0 && (
        <section className="relative py-16 sm:py-20 border-t border-white/10">
          <div className="max-w-4xl mx-auto px-5 sm:px-8">
            <motion.h2 {...fade(0)} className="font-heading text-2xl lg:text-3xl font-bold tracking-tight">What's included</motion.h2>
            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              {page.features.map((f, i) => (
                <motion.div key={f} {...fade(0.04 * i)} data-testid={`seo-feature-${i}`} className="flex items-start gap-3 rounded-xl border border-white/10 bg-card/40 p-4">
                  <Check size={18} weight="bold" className="text-primary mt-0.5 shrink-0" />
                  <span className="text-sm text-foreground/90">{f}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      {page.faq?.length > 0 && (
        <section className="relative py-16 sm:py-20 border-t border-white/10">
          <div className="max-w-3xl mx-auto px-5 sm:px-8">
            <motion.h2 {...fade(0)} className="font-heading text-2xl lg:text-3xl font-bold tracking-tight">Frequently asked questions</motion.h2>
            <div className="mt-8 space-y-3" data-testid="seo-faq-list">
              {page.faq.map((f, i) => (
                <details key={i} data-testid={`seo-faq-item-${i}`} className="group rounded-xl border border-white/10 bg-card/40 px-5 py-4 open:bg-card/70 transition-colors">
                  <summary className="flex items-center justify-between gap-4 cursor-pointer list-none font-heading font-semibold text-base sm:text-lg [&::-webkit-details-marker]:hidden">
                    <span>{f.q}</span>
                    <Plus size={18} weight="bold" className="shrink-0 text-primary transition-transform duration-300 group-open:rotate-45" />
                  </summary>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Related pages */}
      {page.related?.length > 0 && (
        <section className="relative py-16 sm:py-20 border-t border-white/10">
          <div className="max-w-4xl mx-auto px-5 sm:px-8">
            <motion.h2 {...fade(0)} className="font-heading text-2xl lg:text-3xl font-bold tracking-tight">Learn more</motion.h2>
            <div className="mt-8 grid sm:grid-cols-2 gap-4" data-testid="seo-related-links">
              {page.related.filter((r) => SEO_PAGES[r]).map((r, i) => (
                <motion.div key={r} {...fade(0.04 * i)}>
                  <Link
                    to={SEO_PAGES[r].path}
                    data-testid={`seo-related-${r}`}
                    className="group flex items-center justify-between gap-4 rounded-xl border border-white/10 bg-card/40 p-5 hover:border-primary/30 hover:bg-card/70 transition-all"
                  >
                    <span className="font-heading font-semibold text-base">{SEO_PAGES[r].h1}</span>
                    <ArrowRight size={18} weight="bold" className="text-primary shrink-0 transition-transform group-hover:translate-x-0.5" />
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="relative py-16 sm:py-24 border-t border-white/10">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 text-center">
          <motion.h2 {...fade(0)} className="font-heading text-3xl lg:text-4xl font-bold tracking-tight">Ready to take control?</motion.h2>
          <motion.p {...fade(0.06)} className="mt-3 text-muted-foreground">Start free in minutes — no credit card required.</motion.p>
          <motion.div {...fade(0.12)} className="mt-8">
            <Link to={page.cta?.to || "/login"} data-testid="seo-footer-cta" className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground font-semibold px-6 py-3 hover:scale-[1.04] transition-transform">
              {page.cta?.label || "Start free"} <ArrowRight size={17} weight="bold" />
            </Link>
          </motion.div>
        </div>
      </section>

      <SeoFooter />
    </div>
  );
}
